import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import FormControl from '@mui/material/FormControl'

import FormLabel from '@mui/material/FormLabel'

import MDEditor from '@uiw/react-md-editor'

import TagSelector from '../tags/TagSelector'
import TerminologySourcesEditor, { emptySource, validateSources } from '../shared/TerminologySourcesEditor'
import { AdminTerminologyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { updateTerminology } from '../../reducers/terminologyReducer'
import { deleteRevision, acceptRevision } from '../../reducers/revisionReducer'

const hydrateSources = (existing) => {
    if (!existing || existing.length === 0) return [emptySource()]
    return existing.map(s => ({
        source: s.source || '',
        url: s.url || '',
        publication_data: s.publication_data || '',
        pub_date: s.pub_date || '',
    }))
}

const AdminTerminologyRevisionModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state
    const [term, setTerm] = useState('')
    const [termError, setTermError] = useState(false)
    const [termHelper, setTermHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [sources, setSources] = useState([emptySource()])
    const [tags, setTags] = useState([])
    // Guards the Accept button across the two-request accept sequence (PUT the
    // term, then POST accept/) so a second click can't fire either request twice.
    const [submitting, setSubmitting] = useState(false)


    // init fields. For a revision, `props.sources` is the revision's proposed sources.
    useEffect(() => {
        setTerm(props.term)
        setDescription(props.description)
        setSources(hydrateSources(props.sources))
        setTags((props.tags || []).map(String))
    }, [props])


    // basic form validation
    const validate = (field, setError, setHelper) => {
        if(!field || field.length === 0) {
            setError(true)
            setHelper('This field is required')
            return false
        } else {
            setError(false)
            setHelper('')
            return true
        }
    }

    // Apply the revision to the term (PUT, with the revision's edited sources), then --
    // only once that actually succeeded -- accept the revision itself (which deletes it
    // and emails the submitter server-side). Either request failing leaves the modal
    // open with the submitted data intact, rather than silently dropping the revision.
    const handleAccept = async () => {
        const sourcesCheck = validateSources(sources)
        if (!validate(term, setTermError, setTermHelper) || !validate(description, setDescriptionError, setDescriptionHelper)) {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
            return
        }
        if (!sourcesCheck.ok) {
            dispatch(setErrorNotification(sourcesCheck.message))
            return
        }
        if (window.confirm('Are you sure? This will update the existing terminology for everyone.')) {
            const data = {
                term,
                description,
                tags,
            }
            const cleanSources = sources.map(s => ({
                source: s.source,
                url: s.url || null,
                publication_data: s.publication_data || null,
                pub_date: s.pub_date || null,
            }))
            const isAdmin = user.staff || user.terminology_admin

            setSubmitting(true)
            try {
                const updated = await dispatch(updateTerminology(
                    props.terminology, { ...data, sources: cleanSources }, isAdmin,
                ))
                if (!updated) return

                const accepted = await dispatch(acceptRevision(props.id, isAdmin))
                if (!accepted) return

                handleClose()
                if (props.onRevisionHandled) props.onRevisionHandled({ id: props.terminology, ...data })
            } finally {
                setSubmitting(false)
            }
        }
    }

    const handleReject = () => {
        if (window.confirm('Are you sure you want to delete this revision? This action cannot be undone.')) {
            dispatch(deleteRevision(props.id, user.staff || user.terminology_admin))
            handleClose()
            if (props.onRevisionHandled) props.onRevisionHandled()
        }
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            <IconButton {...AdminTerminologyModalStyle.icon.props} sx={AdminTerminologyModalStyle.icon.style} onClick={()=>handleClickOpen()} aria-label="Review revision">
                <EditIcon />
            </IconButton>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`admin-revision-modal-${props.id}`}
                open={open}
                fullWidth
                maxWidth="md"
            >
                <BootstrapDialogTitle id={`admin-revision-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminTerminologyModalStyle.headingContainer}>
                        <MenuBookIcon sx={AdminTerminologyModalStyle.icon}/>
                        Reviewing Revision
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={term}
                                onChange={e => setTerm(e.target.value)}
                                label="Suggested Term"
                                required
                                error={termError}
                                helperText={termHelper}
                                onBlur={()=>validate(term, setTermError, setTermHelper)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel component="legend">Suggested Description (Markdown supported)</FormLabel>
                                <Box sx={{ marginTop: '10px' }}>
                                    <MDEditor
                                        value={description}
                                        onChange={setDescription}
                                        preview="edit"
                                        hideToolbar={false}
                                        data-color-mode="light"
                                    />
                                </Box>
                                {descriptionError && (
                                    <Typography color="error" variant="caption">
                                        {descriptionHelper}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TerminologySourcesEditor sources={sources} onChange={setSources} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box>
                                <TagSelector
                                    tagType="default"
                                    selectedTags={tags}
                                    onChange={setTags}
                                    multiple={true}
                                    label="Tags"
                                    maxTags={5}
                                />
                            </Box>
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleReject} sx={{ color: 'red' }}>
                        Reject
                    </Button>
                    <Button onClick={handleAccept} variant="contained" disabled={submitting}>
                        Accept
                    </Button>
                    <Button onClick={handleClose}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminTerminologyRevisionModal
